/**
 * FS-502 — the operator write path for a homeowner's equipment and filter
 * records, on the firebase API (`/fb/equipment/:serial`).
 *
 * Everything this module reaches is a document the homeowner also sees in the
 * app, so the shapes here are the server contract verbatim: the GET hands back
 * the current claim, the documents under it and the vocabularies their enum
 * fields accept; the PATCH carries the document version it was built from, so
 * a write that raced somebody else's is refused rather than applied.
 *
 * The refusal codes are the reason this file holds copy at all. A write that
 * did not happen has to say which of several very different things went wrong
 * — a duplicate registration, a record that moved, a stale version — because
 * each one needs a different action from the operator.
 */

import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { own } from '../utils/ownKey';

/**
 * The permission that gates every route in this module. The console keeps no
 * static permission registry — the catalog is served by accounts-api at
 * `/accounts/permissions/catalog` and rendered on the Users page — so the name
 * lives here, next to the calls it guards, rather than being spelled inline.
 */
export const EDIT_EQUIPMENT_PERMISSION = 'edit_equipment';

/**
 * Which document a write is aimed at.
 *
 * Plan 120 adds `coil`: the evaporator coil is a component of its own now,
 * with its own Firestore child under the HVAC system. It takes FIELD EDITS
 * ONLY — a photo attaches to the HVAC or A/C document and reaches a coil only
 * through the server's per-photo routing, so there is no photo action on a
 * coil target and no photo-attach block on its tab.
 */
export type EquipmentTargetKind = 'hvac' | 'ac' | 'filter' | 'coil';

/** The targets a photo can be attached to. A coil is never one of them. */
export type EquipmentPhotoTargetKind = Exclude<EquipmentTargetKind, 'coil'>;

/**
 * How the device's app registration stands. Only `current` and `stale` can be
 * written to: `ambiguous` has no single record to correct, and `missing` has
 * no record at all.
 */
export type EquipmentStatus = 'current' | 'ambiguous' | 'stale' | 'missing';

/** One `fieldSources.<field>` entry as the app and the operator path write it. */
export interface EquipmentFieldSource {
  source: string;
  version?: number | null;
  at?: string | null;
  evidence?: string | null;
  confidence?: string | null;
}

/** One app registration on the device. */
export interface EquipmentClaim {
  path: string;
  create_time: string | null;
  is_current: boolean;
  hvac_name: string | null;
}

/**
 * How the label read of the most recent photo on this record is going.
 * `processed_at` is the only thing that says it is over: until it is set the
 * read is still running, and once it is, either `error` says why nothing was
 * filled or the record's own values are the result. Filter records never carry
 * one — their labels are stored, not read.
 */
export interface EquipmentPhotoAnalysis {
  requested_at: string | null;
  processed_at: string | null;
  error: string | null;
  url: string | null;
  /**
   * The fields the read actually wrote, named by the Function that wrote them.
   * The console cannot work this out for itself — it only sees the fields it
   * offers a box for, and a read fills several it does not. An empty list is
   * a read that changed nothing; absent is an API from before the list.
   */
  fields_updated?: string[] | null;
  /**
   * FS-784 — keys the newest request gained. Each is null when the request
   * does not carry it, and absent on an API from before FS-784.
   */
  edit_id?: string | null;
  review_id?: string | null;
  routed_to?: string | null;
  proposed_target?: string | null;
  component?: string | null;
  reason_code?: string | null;
  outcome?: string | null;
}

/**
 * FS-782 — one label-read request on a record, newest first in the list the
 * GET returns. The drawer follows its own upload by `edit_id` rather than by
 * "the newest request on this record": two photos in flight on one record
 * otherwise finish against whichever was uploaded last.
 */
export interface EquipmentPhotoAnalysisRequest {
  edit_id: string | null;
  requested_at: string | null;
  claimed_at?: string | null;
  processed_at: string | null;
  error: string | null;
  rejected?: string | null;
  url: string | null;
  kind?: string | null;
  by?: string | null;
  fields_updated: string[] | null;
  /** `promoted` | `proposed` | `unresolved` | `conflict` | …, or null while running. */
  outcome: string | null;
  // FS-784 additions — null when absent.
  review_id?: string | null;
  /** The record the facts went to when it is not this one (absolute path). */
  routed_to?: string | null;
  proposed_target?: string | null;
  component?: string | null;
  requested_component?: string | null;
  /** The router's reason, e.g. `label_kind_service_sticker`, `no_hvac_document`. */
  reason_code?: string | null;
  preview_error?: string | null;
  stages?: { decode: string | null; catalog: string | null } | null;
  /**
   * FS-783 — the removal that tagged this request (null when none). A request
   * the removal closed before it finished also carries `outcome: "removed"`.
   */
  removal_id?: string | null;
  /**
   * FS-807 — the photo already on the record that this upload was the same
   * as (its URL): the read ran on that photo instead of a second copy. Null
   * when the upload was new; absent on an API from before FS-807.
   */
  duplicate_of?: string | null;
}

/**
 * Why a photo cannot be taken off one record alone (FS-806 Detach), or
 * cannot leave the record it was dropped on when a review is applied
 * elsewhere: values or open questions on that record were read from it, or
 * another read or Apply of it there is still running (an operator's, or a
 * homeowner's photo read still pending). The server's one rule
 * (`moveKeepReason`) decides; the console only says it.
 */
export type PhotoKeepReason = 'source_values_cite_photo' | 'photo_busy' | 'source_evidence_unresolved';

/**
 * One photo on a record, and the copy of it the label read could open.
 *
 * A browser cannot draw a HEIC, so the server keeps the JPEG it rendered to
 * read the label and hands both back. `analyzed_url` is that derivative when
 * there is one and the upload itself when there is not, so `derivative` — not
 * a difference between the two URLs — is what says a conversion happened.
 */
export interface EquipmentLabelPhoto {
  url: string;
  /**
   * FS-1078 — true while a homeowner read of this photo is pending on this
   * record, which refuses Read again; absent otherwise.
   */
  homeowner_read_pending?: boolean;
  analyzed_url?: string | null;
  derivative?: { format?: string; from?: string } | null;
  analyzed_at?: string | null;
  requested_by?: string | null;
  /**
   * FS-780: the image to draw for this photo, chosen on the server — the newest
   * preview recorded on this record, else the newest read's converted copy —
   * and null when that is the upload itself. Unlike `analyzed_url` it is set on
   * a record the photo was routed to, which never had a read of its own.
   * Absent on an API from before FS-780.
   */
  preview_url?: string | null;
  /** What `preview_url` was converted from, e.g. `heic`; null when nothing was. */
  preview_from?: string | null;
  /**
   * FS-783 — every record on the system whose display fields carry this
   * photo (absolute paths). A removal takes it off all of them, so the drawer
   * names them before the operator acts. Absent on an API before FS-783.
   */
  appears_on?: string[];
  /**
   * FS-806 Detach — the OTHER records on the system that carry this same
   * photo (by URL, or by content hash). Non-empty means the photo can be
   * taken off this record alone and stays on those. Absent or empty: there
   * is no Detach on this photo.
   */
  also_on?: string[];
  /**
   * FS-806 Detach — why Detach is refused on this record right now, or null.
   * The control is still drawn, disabled, with this reason on hover.
   */
  detach_blocked?: PhotoKeepReason | null;
  /** FS-1079 — which way up the photo is drawn; null or absent: as it is. */
  rotation?: PhotoRotation | null;
}

/**
 * FS-1079 — the stored turn for a label photo: clockwise degrees from the
 * photo as a browser draws it. An operator's Rotate always wins over the
 * automatic value the server sets after reading the label.
 */
export interface PhotoRotation {
  degrees: number;
  source: 'automatic' | 'operator';
  at: string | null;
  /** The console actor (`operator:u<id>`), for an operator value. */
  by: string | null;
}

// ── FS-783: removing a photo from its records ─────────────────────────────
//
// A removal takes one photo off every record on the system that carries it,
// keeps the file (under a hold) for 90 days, and leaves every value read from
// it in place, marked. Undo puts back exactly what was removed, piece by
// piece, and names any piece it left alone because it changed since. These
// are the server's shapes verbatim (contract §4).

/** Why the photo was removed, as one of the panel's chips. Optional; the reason text is not. */
export type PhotoRemovalReasonKind = 'duplicate' | 'wrong_photo' | 'unreadable';

/**
 * A photo in one of a record's older single-photo fields rather than in
 * `labelPhotoUrls`. The Photos tab always showed these; the drawer did not,
 * so it could not remove them.
 */
export interface EquipmentOtherPhoto {
  url: string;
  field: 'labelPhotoUrl' | 'photoUrl' | 'photoUrls';
  /** FS-1078 — as on `EquipmentLabelPhoto`. */
  homeowner_read_pending?: boolean;
  appears_on?: string[];
  /** FS-806 — as on `EquipmentLabelPhoto`. */
  also_on?: string[];
  detach_blocked?: PhotoKeepReason | null;
  /** FS-1079 — as on `EquipmentLabelPhoto`. */
  rotation?: PhotoRotation | null;
}

/** A photo removed from this record, newest first. */
export interface EquipmentRemovedPhoto {
  removal_id: string;
  url: string;
  /** The newest preview removed with it, for the grey tile; null when none. */
  preview_url: string | null;
  /** FS-1079 — the turn the photo was drawn with when it was removed; absent when none. */
  rotation?: { degrees: number; source: 'automatic' | 'operator' } | null;
  /** Null only on an index entry written without one; the server requires a reason. */
  reason: string | null;
  reason_kind: PhotoRemovalReasonKind | null;
  removed_at: string | null;
  /** `operator:u<id>`. */
  removed_by: string | null;
  /**
   * FS-863 — the sign-in email of the account `removed_by` names. Null when
   * that account no longer exists or `removed_by` is not an account; absent
   * when the server could not look it up (or predates the key).
   */
  removed_by_email?: string | null;
  undo_until: string | null;
  retain_until: string | null;
  /** Computed on the server: the Undo window is still open. */
  undo_available: boolean;
  /**
   * FS-783 addendum 2 F9 — the file's protection as the tombstone records it,
   * per object, so a hold that failed is still visible (with Retry) after the
   * drawer is closed and opened again. Absent on an older API.
   */
  storage?: PhotoStorageState | null;
  /** F9 — how many times the homeowner's app wrote the photo back and the server took it off again. */
  reapplied?: number | null;
  /** F9 — how the device summary refresh after the removal went; null when not recorded. */
  resync?: { sent: boolean; failed: string | null } | null;
}

/**
 * FS-783 addendum 2 F9 — something about a removal that still needs the
 * operator after the removal itself has gone from view: a photo put back
 * whose file protection could not be released, or a removed photo the
 * homeowner's app wrote back (which the server took off again). Kept on the
 * record by the server until it is dismissed.
 */
export interface PhotoRemovalNotice {
  id: string;
  /**
   * `release` (an Undo still owes the file's release; the API writes it), or
   * a re-add the filter trigger recorded. Any kind other than a release is
   * read as a re-add, so the Functions' name for it needs no console change.
   */
  kind: string | null;
  removal_id: string | null;
  url: string | null;
  /** `release`: how the release stands when the server has no per-object state. */
  status?: string | null;
  at: string | null;
  /** `readded`: how many times it was written back. */
  count?: number | null;
  /** `release_failed`: the protection as it stands. */
  storage?: PhotoStorageState | null;
}

/** Why a field's value is marked: the removal that took its evidence away. */
export interface EquipmentEvidenceRemoved {
  removal_id: string;
  reason: string | null;
  reason_kind: PhotoRemovalReasonKind | null;
  removed_at: string | null;
}

/**
 * Fields whose value was worked out together from one photo: clearing one and
 * leaving the other is the partial edit the server refuses
 * (`companion_required`). The same table the server holds.
 */
export const COMPANION_FIELDS: Record<string, string[]> = {
  tonnage: ['capacitySource'],
  capacitySource: ['tonnage'],
};

export interface PhotoRemovalBody {
  /** A fresh id per attempt; a network retry of the same attempt reuses it. */
  edit_id: string;
  target: { kind: EquipmentTargetKind; path: string };
  url: string;
  reason: string;
  reason_kind?: PhotoRemovalReasonKind;
}

/** How the protection on the file stands after a removal. */
export type PhotoFileHold = 'set' | 'partial' | 'failed' | 'not_applicable';

export interface PhotoRemovalResult {
  removal_id: string;
  url: string;
  state: 'removed';
  records: Array<{
    path: string;
    kind: EquipmentTargetKind;
    name: string | null;
    /** The pieces taken off this record, by field name. */
    removed: string[];
    /** The record's single-photo field after the removal, or null when it has none. */
    singular_now: string | null;
  }>;
  reviews_superseded: string[];
  /** Label-read requests the removal closed before they finished. */
  requests_closed: string[];
  conflicts_withdrawn: Array<{ path: string; field: string }>;
  evidence_removed: Array<{ path: string; field: string; value: unknown }>;
  uncertain: Array<{ path: string; field: string; why: string }>;
  undo_until: string;
  retain_until: string;
  file_hold: PhotoFileHold;
  /** Addendum 2 F10 — the protection per object (original vs preview; `missing` apart from `failed`). */
  file_objects?: PhotoStorageState['objects'] | null;
  resync: { sent: boolean; failed: string | null };
}

export interface PhotoUndoBody {
  edit_id: string;
  url: string;
  reason?: string;
}

export interface PhotoUndoResult {
  removal_id: string;
  state: 'restored';
  records: Array<{ path: string; restored: string[]; skipped: Array<{ piece: string; code: string }> }>;
  reviews_restored: string[];
  reviews_not_restored: Array<{ review_id: string; code: string }>;
  /** Label-read requests the removal had stopped; Undo never re-runs them. */
  reads_not_restored: string[];
  /**
   * Addendum 3 N3 — `file_hold_released`, `file_objects` and `resync` are
   * absent from an Undo whose post-commit work had not finished when its
   * answer was stored (a replay of an interrupted Undo). The photo is back;
   * the release and the device summary refresh are still to be reported.
   */
  file_hold_released?: boolean;
  /** Addendum 2 F10 — the release per object. */
  file_objects?: PhotoStorageState['objects'] | null;
  resync?: { sent: boolean; failed: string | null };
}

/**
 * Addendum 2 F17 — what an `already_restored` refusal carries: the Undo that
 * did put the photo back, so a retry whose first answer was lost can show it.
 */
export interface PhotoLastUndo {
  edit_id: string | null;
  at: string | null;
  /** Addendum 3 N3 — `committed` until the release and resync after it have finished. */
  status?: 'committed' | 'complete' | null;
  response: PhotoUndoResult | null;
}

/** The tombstone's `storage` block, which the protection retry returns. */
export interface PhotoStorageState {
  hold: 'pending' | 'set' | 'partial' | 'failed' | 'not_applicable' | 'released' | 'release_failed' | null;
  objects: Array<{
    path: string;
    /** `late_copy` (addendum 3): a converted copy made after the photo was removed. */
    role: 'original' | 'preview' | 'late_copy';
    status: 'pending' | 'set' | 'missing' | 'failed' | 'released' | 'release_failed' | 'not_applicable';
    error?: string | null;
  }>;
  at: string | null;
}

/** A document that can be edited: its values, where each value came from, and its version. */
export interface EquipmentDocument {
  path: string;
  update_time: string;
  fields: Record<string, unknown>;
  field_sources: Record<string, EquipmentFieldSource>;
  label_photo_urls?: string[];
  /** The same photos with their converted copies. Absent on an older API. */
  label_photos?: EquipmentLabelPhoto[];
  /** hvac and ac records only, and only after a photo has been attached. */
  photo_analysis?: EquipmentPhotoAnalysis | null;
  /**
   * FS-782 — every label-read request on this record, newest first. hvac and
   * ac records only; absent on an API that predates the list, in which case
   * `photo_analysis` (the newest) is all there is to follow.
   */
  photo_analysis_requests?: EquipmentPhotoAnalysisRequest[];
  /** A/C children only — the record the resolver reads this device's facts off. */
  is_resolver_pick?: boolean;
  /** Filters only — the one the current device document points at. */
  is_current?: boolean;
  // FS-593 — filters only, and absent on an API before FS-593. Each filter
  // record is one slot on the system (a return grille or the furnace filter).
  /** The filter document id; sent back as `filterId` to edit this slot. */
  id?: string;
  /** `airReturns` | `furnace`, or null when the app recorded no type. */
  filter_type?: string | null;
  /** The homeowner's name for the slot, or null. */
  location?: string | null;
  has_sensor?: boolean | null;
  /** The Clip serial recorded in the slot (lower-case), or null for no Clip. */
  device_name?: string | null;
  created_at?: string | null;
  /** This Clip's current filter. */
  own?: boolean;
  // FS-783 — each absent on an API from before photo removal.
  /** Photos in the older single-photo fields, not in `label_photo_urls`. */
  other_photo_urls?: EquipmentOtherPhoto[];
  /** Photos removed from this record, newest first. */
  removed_photos?: EquipmentRemovedPhoto[];
  /** Fields whose value was read from a photo that was since removed. */
  field_evidence_removed?: Record<string, EquipmentEvidenceRemoved>;
  /** Fields read from a photo whose stamp did not record which one (only after a removal). */
  uncertain_evidence_fields?: string[];
  /** Addendum 2 F9 — what still needs the operator about this record's removals. */
  photo_removal_notices?: PhotoRemovalNotice[];
}

/** The exact vocabularies the server will accept for the enum fields. */
export interface EquipmentOptions {
  motor_types: string[];
  equipment_sides: string[];
  capacity_sources: string[];
  /**
   * FS-784 — true when an operator photo is read into a review instead of
   * filling the record on its own. Absent on an older API, which reads as off.
   */
  photo_review?: boolean;
  /**
   * FS-783 — true when photos can be removed from records. Absent on an older
   * API, which reads as off: the Remove control is not drawn.
   */
  photo_removal?: boolean;
  /**
   * FS-806 / FS-807 — true when an applied photo moves to its record, an
   * identical photo is not stored twice, and a photo can be taken off one
   * record alone (Detach). Absent on an older API, which reads as off: the
   * "Also keep it" box and Detach are not drawn.
   */
  photo_placement?: boolean;
}

/**
 * The app registration the device is using now. `device_update_time` is the
 * device document's own version, and it is the one a filter change is checked
 * against: recording a filter repoints that document, so the write races an
 * app that is doing the same thing, not an edit to the filter record.
 */
export interface EquipmentCurrentClaim {
  path: string;
  create_time: string | null;
  filter_id: string | null;
  device_update_time: string | null;
}

export interface EquipmentRecord {
  serial: string;
  status: EquipmentStatus;
  claim_count: number;
  current_claim: EquipmentCurrentClaim | null;
  claims: EquipmentClaim[];
  hvac: EquipmentDocument | null;
  ac_systems: EquipmentDocument[];
  filters: EquipmentDocument[];
  /**
   * Plan 120 — the coil children of the current HVAC system. Absent on an API
   * from before Plan 120, which reads the same as a system with no coil.
   */
  coils?: EquipmentDocument[];
  options: EquipmentOptions;
  /**
   * FS-784 — the open photo reviews on this system, oldest first (at most
   * 20). Absent on an API from before FS-784.
   */
  photo_reviews?: PhotoReviewSummary[];
  /**
   * FS-876 — held reviews an operator dismissed whose record still carries
   * the model; each record card offers "Review again". Absent when none.
   */
  dismissed_reviews?: DismissedReviewSummary[];
}

/** One field's before/after, as the dry run and the applied write both report it. */
export interface EquipmentChange {
  before: unknown;
  before_source: string | null;
  after: unknown;
}

export interface EquipmentPatchBody {
  target: { kind: EquipmentTargetKind; path: string };
  expected_update_time: string;
  fields: Record<string, unknown>;
  reason: string;
  dry_run?: boolean;
  /**
   * FS-783 addendum 2 F12 — fields whose value on file is still right: kept
   * as it is and re-stamped as the operator's entry, which ends their
   * "evidence removed" mark. Sent only when non-empty.
   */
  confirm?: string[];
  /**
   * FS-593 — kind `filter` only: the slot to edit, by document id. Omitted for
   * the Clip's own filter. Another slot takes only `filterType` and
   * `location`, and only when no other Clip is recorded in it.
   */
  filterId?: string;
}

export interface FilterChangeBody {
  filter: {
    manufacturer: string;
    rating: string;
    length: number;
    width: number;
    thickness: number;
    modelNumber?: string;
    installDate: string;
    isNewFilter: boolean;
  };
  /**
   * The device document's `device_update_time` from the GET. The server runs
   * the create, the device repoint and the location count in one transaction
   * against it, so a device that moved in between is refused with nothing
   * written. Required on the dry run too — a preview against a version that no
   * longer exists is not a preview of the write that would happen.
   */
  expected_update_time: string;
  reason: string;
  dry_run?: boolean;
}

export interface EquipmentEditResult {
  /** Absent on a dry run — nothing was written, so there is no edit to identify. */
  edit_id?: string;
  target: { kind: EquipmentTargetKind; path: string };
  changes: Record<string, EquipmentChange>;
  sync?: { enqueued: boolean; message_id: string | null };
  audit_mirrored?: boolean;
  /** The filter create route only. */
  filter_path?: string;
}

/**
 * FS-838 — the five ways FS-837 F30 holds a label's cooling capacity back.
 * Each refuses the WHOLE Apply: nothing is written, and the refusal's
 * `fields` lists the ticked capacity fields. `{fields}` is filled with those,
 * in words, so the operator knows exactly what to untick.
 */
const CAPACITY_HELD_TEMPLATES: Record<string, string> = {
  capacity_held_target_model_furnace:
    "With this selection the record's model is a furnace in the equipment catalog, and a furnace's label never gives the system's cooling size. Nothing was changed. Untick the {fields}, or tick the label's model number if this record is really the air handler on the label.",
  capacity_held_target_model_unverified:
    'This record keeps a model number the equipment catalog could not look up, so it cannot tell whether the unit is a furnace. Nothing was changed. Untick the {fields}, or tick the label\'s model number as well.',
  capacity_held_photo_furnace:
    "This label is a furnace's, and a furnace's label never gives the system's cooling size. Nothing was changed. Untick the {fields}.",
  capacity_held_identity_mismatch:
    "The label and this record name different kinds of indoor unit (for example an air handler and a ductless head), so its cooling size cannot be put on this record. Nothing was changed. Untick the {fields}, or tick the label's model number as well.",
  capacity_held_target_burner:
    "With this selection the record has a burner (a BTU input rating or a gas, propane or oil fuel type), so it is a furnace, and a furnace's label never gives the system's cooling size. Nothing was changed. Untick the {fields}, or untick the burner values.",
};

/** The capacity fields a hold can name, as an operator calls them. */
const CAPACITY_FIELD_WORDS: Record<string, string> = {
  tonnage: 'tonnage',
  capacitySource: 'how the capacity was determined',
  refrigerantType: 'refrigerant',
};

/** "tonnage", "tonnage and refrigerant", "a, b and c"; "capacity values" for none. */
const capacityFieldsText = (fields: readonly string[] | null | undefined): string => {
  const words = (Array.isArray(fields) ? fields : [])
    .filter((f): f is string => typeof f === 'string' && f.length > 0)
    .map((f) => own(CAPACITY_FIELD_WORDS, f) ?? f);
  if (words.length === 0) return 'capacity values';
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

/**
 * The sentence for a `capacity_held_*` refusal, naming the ticked fields; null
 * for any other code. Both the Apply's HTTP refusal and a refusal recorded on
 * the review (`last_apply`) try this first.
 */
export function capacityHeldMessage(
  code: string | null | undefined,
  fields?: readonly string[] | null,
): string | null {
  const template = own(CAPACITY_HELD_TEMPLATES, code);
  return template ? template.replace('{fields}', capacityFieldsText(fields)) : null;
}

/**
 * One sentence per refusal the server can send, each naming what to do next.
 * The client raises four of them on its own before any request goes out —
 * `reason_required`, `no_changes`, `no_current_filter` and
 * `missing_device_version` — so an operator reads the same wording whichever
 * side caught it.
 */
export const EQUIPMENT_EDIT_ERRORS: Record<string, string> = {
  ambiguous_claims:
    'This device has more than one app registration, so there is no single record to edit. Clean up the duplicate registrations on the App Claims tab first.',
  target_not_under_current_claim:
    'That record is not part of the app registration this device is using now. Close the drawer and open it again to load the current records.',
  stale_document:
    'Someone else changed this record while it was open. Close the drawer and open it again to load the latest values, then make the change again.',
  no_current_filter:
    'This device has no filter record to correct. Use the filter change form below to record one.',
  duplicate_edit_id:
    'This edit was already submitted; reload and try again if it did not apply.',
  missing_device_version:
    'The device record came back without a version, so a filter change cannot be recorded safely. Close the drawer and open it again.',
  invalid_field:
    'One of the fields sent cannot be edited on this record. Close the drawer and open it again, then make the change again.',
  invalid_value:
    'One of the values is not one this record accepts. Pick a value from the list, or check the number that was typed.',
  // Plan 123 §4.1 — the two ways a declared absence is refused. Each needs a
  // different thing from the operator, so neither reads as "invalid value".
  absent_not_applicable:
    'That part cannot be declared absent on this record. A packaged unit is one machine, and a filter belongs to a single Clip, so neither can be declared absent for the whole system.',
  applicability_conflict:
    'The record already holds a fact or a photo for that part, so it cannot be declared absent. Open the Equipment Inventory to see what is on file, and correct that first.',
  blower_not_applicable:
    'This record is for a unit with no blower, so a motor type cannot be recorded on it. To move it to a coil or an outdoor unit, clear the motor type in the same edit.',
  no_changes: 'Nothing was changed. Edit at least one value before saving.',
  reason_required:
    'Give a reason of at least 8 characters explaining why this record is being corrected.',
  invalid_file:
    'The server would not accept that file, so no photo was attached. Attach a photo of the label: a JPEG, PNG or HEIC image under 20 MB.',
  target_not_found:
    'That record is no longer on file, so the photo was not attached. Close the drawer and open it again to see the records that are there now.',
  // FS-593 — the filter-slot refusals. The server's own sentence is shown
  // when it sends one (it names the Clip); these stand in when it does not.
  slot_belongs_to_another_clip:
    'Another Clip is recorded in this filter slot, so it is edited from that Clip\'s page. Nothing was saved.',
  slot_field_not_editable:
    'Only the filter type and name can be changed on this filter record. Nothing was saved.',
  slot_not_found:
    'That filter record is no longer on this system. Close the drawer and open it again to load the current records.',
  // FS-784 — the photo review refusals (§6.3–§6.5), and the codes the apply
  // step can leave in `last_apply`. Every one says that nothing was written,
  // because none of them writes anything.
  review_not_found:
    'That photo review is no longer on file. Close the drawer and open it again to see the reviews that are there now.',
  review_not_under_current_claim:
    'That photo review belongs to an app registration this device is no longer using. Nothing was changed.',
  review_not_open:
    'This photo review has already been finished or is being applied. Nothing more was changed; close it and open the record again.',
  proposal_changed:
    'The values read from this photo were refreshed while the review was open. Nothing was changed; check the values again.',
  proposal_outdated:
    'The record or the values on offer changed since this review was prepared, so nothing was changed and the review was refreshed. Check the values again.',
  target_blocked:
    'A person set that record as a different part of the system than these values are for, so nothing was changed.',
  photo_removed:
    'This photo is no longer on the record, so nothing was changed.',
  unknown_candidate:
    'One of the ticked values is not part of this review any more. Nothing was changed; close the review and open it again.',
  stale_fields:
    'Some values changed since you opened this. Nothing was changed; check the highlighted rows.',
  field_locked:
    'One of the ticked values was entered or confirmed by a person, so nothing was changed. Change it with the field editor.',
  dependency_not_selected:
    'A ticked value depends on another value that is not ticked. Nothing was changed; tick both or neither.',
  identity_required:
    "This label's model differs from the record's, so its other values can only be used with the model number. Nothing was changed; tick the model number too.",
  required_row_missing:
    'A new record is created as the component on the label, so that value has to be ticked. Nothing was changed.',
  nothing_selected:
    'No values are ticked. Nothing was changed; tick at least one value, or choose a different record to attach the photo to.',
  unverified_apply:
    'The apply request could not be matched to its audit record, so nothing was changed. Try again.',
  apply_retry_exhausted:
    'Applying the values did not finish, so nothing was changed. Try again.',
  review_not_created:
    'The server could not start a review for this photo, so the photo was not uploaded. Try again.',
  // Addendum 1 — the registration moved, or the review was already closed.
  registration_changed:
    'This Clip was registered to a different system since the photo was read; nothing was changed.',
  review_closed:
    'This photo review was already dismissed or turned down, so nothing was changed. Upload the photo again to review it.',
  request_removed:
    'The photo request was removed before its label was read, so nothing was read from it.',
  no_hvac_document:
    'This system no longer has an HVAC record, so nothing was changed.',
  review_record_mismatch:
    'The photo review did not match the photo request, so the label was not read into it.',
  // FS-787 — catalog reviews: the record a catalog value was proposed for is
  // gone, or no longer carries the model the values belong to.
  record_removed:
    'The record these catalog values were for is no longer on file, so nothing was changed.',
  source_removed:
    'The record these values were for is no longer on file, so nothing was changed.',
  model_changed:
    'The model number on the record changed since the catalog values were prepared, so nothing was changed.',
  retry_window_exhausted:
    "The catalog values couldn't be prepared in time. Nothing was changed.",
  // FS-783 — removing a photo, and undoing a removal. `target_not_under_current_claim`
  // above covers a removal aimed at a record the registration no longer has.
  photo_removal_off:
    'Removing photos is switched off on the server right now. Nothing was removed.',
  photo_not_on_record:
    'That photo is no longer on this record, so nothing was removed. Close the drawer and open it again to see the photos there now.',
  photo_already_removed:
    'That photo was already removed, so nothing more was changed. Close the drawer and open it again to see the removal and its Undo.',
  family_too_large:
    'This system has too many records of one kind for the photo to be removed safely, so nothing was removed. Ask for help with this system.',
  removal_too_large:
    'This photo carries too much with it to be removed in one step, so nothing was removed. Ask for help with this system.',
  removal_not_found:
    'That removal is no longer on file, so nothing was put back. Close the drawer and open it again.',
  already_restored:
    'That photo was already put back, so nothing more was changed.',
  removal_not_under_current_claim:
    'That removal was made under an app registration this device is no longer using, so nothing was put back.',
  undo_expired:
    'The time to undo this removal has passed, so nothing was put back. The file is still kept until its retention date.',
  companion_required:
    'Two of these values were worked out together from the same removed photo. Set or clear both in the same save. Nothing was saved.',
  model_evidence_removed:
    'The model number on this record came from a photo that was removed. Check the model, then the catalog values can be offered again.',
  undo_too_large: 'Undo would make this record too large; nothing was changed.',
  too_many_open_reviews:
    'This photo has too many photo reviews open to close them all safely, so nothing was removed. Finish or dismiss some of its reviews, then try again.',
  // FS-806 / FS-807 — a photo that moves with its review, an identical photo
  // not stored twice, and taking a photo off one record alone (Detach).
  photo_changed:
    "The record's photos changed while this one was uploading. Nothing was stored; try again.",
  photo_moved:
    'This photo was moved to another record by another review of the same photo; nothing was changed here.',
  photo_placement_off:
    'Moving photos between records is switched off on the server right now. Nothing was changed.',
  no_other_carrier:
    'No other record on this system carries this photo, so it cannot be taken off this record alone. Use Remove to take it off. Nothing was changed.',
  source_values_cite_photo:
    'Values or open questions on this record were read from this photo, so it stays on this record. Remove it instead if it is wrong. Nothing was changed.',
  // Values reached this record from the photo through an older Apply, and
  // their source cannot be tied to another copy, so the photo stays.
  source_evidence_unresolved:
    "Some values on this record were read from this photo earlier and can't be traced to another copy, so it stays here. Nothing was changed.",
  photo_busy:
    'A label read or Apply of this photo is still running, so it stays on this record for now. Try again in a few minutes. Nothing was changed.',
  // FS-876 — fixing a model on the wrong record, and opening a dismissed
  // review again.
  fix_not_available:
    'This review has nothing it can clear or move any more, so nothing was changed. Close it and open the record again.',
  review_not_reopenable:
    'Only a dismissed review of a model on the wrong record can be reviewed again. Nothing was changed.',
  fix_too_large:
    'These records are too large to change in one step, so nothing was changed. Correct the values by hand in their cards.',
  also_on_other_record:
    'The same photo is on another record of this system, so removing it here would take it off that one too. Nothing was changed; take it off this record with Detach instead.',
  // FS-838 — FS-837 F30's capacity holds, for a caller that reads this map
  // directly; `capacityHeldMessage` names the actual fields instead.
  ...Object.fromEntries(
    Object.keys(CAPACITY_HELD_TEMPLATES).map((code) => [code, capacityHeldMessage(code, []) as string]),
  ),
};

/**
 * FS-783 — refusals whose stock sentence is about something else. The
 * removal routes check the registration when they accept (addendum 2 F1), and
 * the shared `registration_changed` sentence talks about a photo being read.
 */
const PHOTO_REMOVAL_ERRORS: Record<string, string> = {
  registration_changed:
    'This Clip was registered to a different system since the drawer was opened, so nothing was changed. Close the drawer and open it again to see the records it uses now.',
};

/** FS-593 — refusals whose server message is more specific than the stock sentence. */
const SERVER_WORDED_CODES = new Set([
  'slot_belongs_to_another_clip',
  'slot_field_not_editable',
  'slot_not_found',
]);

/** The shortest reason the server accepts, enforced here too so the field can say so. */
export const MIN_REASON_LENGTH = 8;

const equipmentUrl = (serial: string) =>
  `${config.firebaseApiUrl}/fb/equipment/${encodeURIComponent(serial)}`;

export async function getEquipment(serial: string): Promise<EquipmentRecord> {
  const res = await axiosInstance.get<EquipmentRecord>(equipmentUrl(serial));
  return res.data;
}

export async function patchEquipment(
  serial: string,
  body: EquipmentPatchBody,
): Promise<EquipmentEditResult> {
  const res = await axiosInstance.patch<EquipmentEditResult>(equipmentUrl(serial), body);
  return res.data;
}

export async function createFilterRecord(
  serial: string,
  body: FilterChangeBody,
): Promise<EquipmentEditResult> {
  const res = await axiosInstance.post<EquipmentEditResult>(
    `${equipmentUrl(serial)}/filters`,
    body,
  );
  return res.data;
}

/**
 * What the server did with the photo. `requested` means a label read is
 * running behind it and the record's values may change; `stored_only` means
 * the photo is filed against the record and nothing else will happen.
 */
export type EquipmentPhotoDisposition = 'requested' | 'stored_only';

export interface EquipmentPhotoResult {
  edit_id: string;
  target: string;
  photo_url: string;
  analysis: EquipmentPhotoDisposition;
  audit: { status: string };
  /**
   * FS-784 — the review this photo's read goes into (the edit id), or null
   * when the read fills the record on its own. Absent on an older API.
   */
  review_id?: string | null;
  /**
   * FS-807 — the photo already on the record whose content this upload
   * matched: nothing new was stored, and `photo_url` is that photo, which the
   * read runs on. Null when the upload was new; absent on an older API.
   */
  duplicate_of?: string | null;
}

/**
 * FS-781 — what an operator says a photo dropped on an empty Outdoor or Coil
 * tab is of. Sent only with `target=hvac`; the server records it and compares
 * it with the read, but never lets it decide the component.
 */
export type PhotoComponentHint = 'outdoor_condenser' | 'indoor_coil';

/** The server's own cap, checked here so an operator is told before the upload. */
export const MAX_PHOTO_BYTES = 20 * 1024 * 1024;

/**
 * What the file chooser offers. `image/*` alone hides the iPhone photos on a
 * machine whose OS does not map `.heic` to an image type, so the two
 * extensions are named as well.
 */
export const PHOTO_ACCEPT = 'image/*,.heic,.heif';

/**
 * An iPhone photo the browser would not name. `image/heic` and `image/heif`
 * pass the ordinary `image/` test; these are the same files arriving through a
 * share sheet, a zip or a Windows browser with no HEIC mapping, where the type
 * is `application/octet-stream` or simply empty. The extension is all there is
 * to go on, and the server sniffs the bytes anyway before it converts.
 */
export function isHeicByName(file: File): boolean {
  if (!/\.(heic|heif)$/i.test(file.name)) return false;
  return file.type === '' || file.type === 'application/octet-stream';
}

/**
 * A file size the way an operator would say it. Every message about a photo
 * carries one, because size is what decides whether the upload gets through.
 */
export function photoSizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

/**
 * Why this file cannot be sent, or null. Both checks are the server's, run
 * early: a 12 MB photo that fails after a slow upload tells the operator the
 * same thing a second later and wastes the wait.
 */
export function photoFileError(file: File): string | null {
  if (!file.type.startsWith('image/') && !isHeicByName(file)) {
    return `That file is not an image (${file.type || 'no file type'}). Choose a photo of the label — a JPEG, PNG or HEIC file.`;
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return `That photo is ${photoSizeLabel(file.size)}. The limit is 20 MB — take it again at a lower resolution, or choose a smaller file.`;
  }
  return null;
}

/**
 * What goes in the audit record when the operator typed nothing. Attaching a
 * photo is not a change to a value — it is evidence, and the act of adding it
 * is the whole reason — so it does not need explaining the way an edit to a
 * field the homeowner can see does.
 */
export const PHOTO_DEFAULT_REASON = 'image added';

/** FS-1078 — the same, for a Read again: nothing is added, a stored label is read again. */
export const PHOTO_REREAD_DEFAULT_REASON = 'label read again';

/** The reason a photo will carry: what was typed, if it is long enough to keep. */
export function photoReason(typed: string, fallback: string = PHOTO_DEFAULT_REASON): string {
  const trimmed = typed.trim();
  return trimmed.length >= MIN_REASON_LENGTH ? trimmed : fallback;
}

/**
 * Which record the photo is for, in the server's wording: the furnace record
 * is just `hvac`, and an A/C or filter is named by its document id because a
 * home can have several of each.
 */
export function photoTarget(kind: EquipmentPhotoTargetKind, path: string): string {
  if (kind === 'hvac') return 'hvac';
  const id = path.split('/').filter(Boolean).pop() ?? '';
  return `${kind}:${id}`;
}

/**
 * The id that makes the upload safe to retry: a second POST carrying one the
 * server has already seen is refused as a duplicate rather than filing the
 * photo twice.
 */
export const newEditId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `edit-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

export async function uploadEquipmentPhoto(
  serial: string,
  input: {
    target: string;
    file: File;
    reason: string;
    editId: string;
    /** FS-781 — only with `target=hvac`; the server refuses it anywhere else. */
    componentHint?: PhotoComponentHint;
  },
): Promise<EquipmentPhotoResult> {
  const form = new FormData();
  form.append('target', input.target);
  form.append('reason', input.reason);
  form.append('edit_id', input.editId);
  if (input.componentHint) form.append('component_hint', input.componentHint);
  form.append('file', input.file);
  // No Content-Type of our own: axios writes multipart/form-data with the
  // boundary this FormData generated, and one set by hand loses the boundary.
  const res = await axiosInstance.post<EquipmentPhotoResult>(
    `${equipmentUrl(serial)}/photos`,
    form,
  );
  return res.data;
}

/**
 * FS-1078 — Read again: the current reader run on a photo the record already
 * carries, into a review on that same photo. Nothing is stored or added; the
 * answer has the upload's shape, with `review_id` to follow.
 */
export async function rereadEquipmentPhoto(
  serial: string,
  input: {
    target: string;
    url: string;
    reason: string;
    editId: string;
    componentHint?: PhotoComponentHint;
  },
): Promise<EquipmentPhotoResult> {
  const res = await axiosInstance.post<EquipmentPhotoResult>(`${equipmentUrl(serial)}/photos/reread`, {
    target: input.target,
    url: input.url,
    reason: input.reason,
    edit_id: input.editId,
    ...(input.componentHint ? { component_hint: input.componentHint } : {}),
  });
  return res.data;
}

// ── FS-784: photo reviews ────────────────────────────────────────────────
//
// An operator photo, when the server's review mode is on, is read into a
// review instead of filling the record on its own. The read writes nothing;
// the operator picks a target and the values to use, and Apply writes exactly
// those. These are the server's shapes verbatim (contract §6.2–§6.5).
//
// The browser sends ids and opaque tokens, never values or stamps: every
// value Apply writes is the one the server stored when it read the label, so
// nothing typed or altered here can reach the record through this path.

export type PhotoReviewStatus =
  | 'reading'
  | 'ready_for_review'
  | 'held'
  | 'applying'
  | 'applied'
  | 'declined'
  | 'failed'
  | 'superseded';

/** Which tab the photo was dropped on: the source record, or the hint's tab. */
export type PhotoReviewTab = 'hvac' | 'ac' | 'coil';

/**
 * FS-787 — what a review is about: a label photo, or the values the equipment
 * catalog has for a model a record carries. The server sends `kind` only on
 * catalog reviews; absent means `photo` (every FS-784 review).
 */
export type PhotoReviewKind = 'photo' | 'catalog';

/** The catalog model a catalog review is about, as the banner names it. */
export interface CatalogReviewModel {
  model_id: number | null;
  model_display: string | null;
  manufacturer: string | null;
  equipment_type: string | null;
}

/** Where a catalog value came from: the manufacturer document and the spot in it. */
export interface CatalogProvenance {
  document_id: number | null;
  locator: string | null;
  fact: string | null;
  fact_kind: string | null;
  confidence_label: string | null;
  verified_by_person: boolean;
}

/** FS-787 R4 — the Clip a catalog review is bound to, and whether it is still on the system. */
export interface CatalogReviewBinding {
  serial: string | null;
  current: boolean;
}

/** A review's kind, reading an absent `kind` as a photo review. */
export const reviewKindOf = (review: { kind?: PhotoReviewKind | null } | null | undefined): PhotoReviewKind =>
  review?.kind === 'catalog' ? 'catalog' : 'photo';

export interface PhotoReviewLastApply {
  apply_id: string;
  result: 'applied' | 'refused' | 'failed' | 'superseded';
  code: string | null;
  fields?: string[] | null;
  at?: string | null;
}

/** One open review on the system, as the record GET lists them. */
export interface PhotoReviewSummary {
  review_id: string;
  status: PhotoReviewStatus;
  version: number;
  source_path: string;
  dropped_on: PhotoReviewTab;
  hint: PhotoComponentHint | null;
  component: string | null;
  /** Why a held review cannot be applied (the router's reason), else null. */
  hold: { reason: string } | null;
  default_target_path: string | null;
  /** Null for a catalog review, which has no photo. */
  photo_url: string | null;
  preview_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_apply: PhotoReviewLastApply | null;
  stuck: boolean;
  /** FS-787 — only on catalog reviews. */
  kind?: PhotoReviewKind;
  catalog?: CatalogReviewModel | null;
  /**
   * FS-787 — the Clip whose registration a catalog review is bound to. Only
   * that Clip's drawer can apply it; another Clip on the same system sees it
   * as information.
   */
  applies_from_serial?: string | null;
  /**
   * FS-787 R4 — the bound Clip, and whether its registration is still on this
   * system. When it is not, the Clip whose drawer is open can take the review
   * over (`rebindCatalogReview`).
   */
  binding?: CatalogReviewBinding | null;
}

export type PhotoReviewRowState =
  | 'selectable'
  /**
   * FS-1076 — a photo row against a value a person decided: tickable, to
   * replace it, and never ticked by default. A catalog row against one is
   * `locked_person_value`.
   */
  | 'person_value'
  | 'locked_person_value'
  | 'locked_required'
  | 'not_found';

export type PhotoReviewFlag = 'low_confidence' | 'below_identity_bar' | 'converted' | 'required';

/** One proposed value against the target's value now (§6.3). */
export interface PhotoReviewRow {
  candidate_id: string;
  field: string;
  proposed: {
    value: unknown;
    source: string | null;
    basis: string | null;
    confidence: number | null;
    read_value: unknown;
    derived_from: { field: string; value: unknown } | null;
    flags: PhotoReviewFlag[];
    /** FS-787 — only on catalog rows. */
    provenance?: CatalogProvenance | null;
  } | null;
  alternatives: Array<{ source: string; value: unknown; confidence: number | null }>;
  current: {
    value: unknown;
    source: string | null;
    confirmed_by: string | null;
    person_decided: boolean;
    /** FS-783 — the value on file was read from a photo since removed; null or absent when not. */
    evidence_removed?: EquipmentEvidenceRemoved | null;
    /** FS-1076 — on a photo row whose value a person decided: who decided it. */
    decided_by?: 'owner' | 'operator';
    /** FS-1076 — and when, as an ISO instant; null when the record does not say. */
    decided_at?: string | null;
  } | null;
  relation: 'new' | 'same' | 'different' | 'not_found';
  state: PhotoReviewRowState;
  /** Computed by the server only; the dialog starts from it as given. */
  default_checked: boolean;
  depends_on: string | null;
  requires_identity: boolean;
  /** Opaque: sent back with the selection so the server can tell the value moved. */
  current_token: string;
}

export interface PhotoReviewTarget {
  /** Relative to the HVAC system: `hvac` | `ac_systems/<id>` | `coils/<id>`. */
  id: string;
  path: string;
  kind: PhotoReviewTab;
  creates: boolean;
  exists_now: boolean;
  name: string | null;
  current_model: string | null;
  match: { component: string; identity: string };
  blocked: string | null;
  /**
   * FS-787 R10 — catalog targets only: whether Apply will mark the record's
   * model number as catalog-verified (the Functions' own condition).
   */
  model_link_update?: boolean;
  /**
   * FS-807 — photo reviews only: the photo this record already carries that
   * is the same as the review's (by URL or content), so Apply adds no second
   * copy. Null when it has none; absent on an older API.
   */
  duplicate_of?: string | null;
  rows: PhotoReviewRow[];
}

/** FS-806 — where the photo ended up after an Apply to another record. */
export interface PhotoReviewPlacementDecision {
  moved: boolean;
  kept_on_source: boolean;
  /** Set when the photo had to stay on the record it was dropped on. */
  keep_reason: PhotoKeepReason | null;
  /** The photo the target already carried, when it was the same one. */
  duplicate_of: string | null;
  /** Other open reviews of the same photo that the move closed. */
  siblings_superseded: string[];
}

export interface PhotoReviewDecision {
  /** FS-876 adds `cleared` and `moved`: a held catalog review closed by its fix. */
  kind: 'applied' | 'declined' | 'dismissed' | 'cleared' | 'moved';
  at: string | null;
  target_path: string | null;
  created: boolean;
  fields_written: string[];
  rows: Array<{ candidate_id: string; field: string; decision: string }>;
  /** FS-806 — absent on an older API; null for a catalog review. */
  placement?: PhotoReviewPlacementDecision | null;
}

/** The full review (§6.3). The status view is the same without `targets`. */
export interface PhotoReview {
  review_id: string;
  edit_id: string;
  status: PhotoReviewStatus;
  open: boolean;
  version: number;
  created_at: string | null;
  updated_at: string | null;
  /** `coil` only on a catalog review of a coil record. */
  source: { path: string; kind: 'hvac' | 'ac' | 'coil' };
  dropped_on: PhotoReviewTab;
  hint: PhotoComponentHint | null;
  hint_matches: boolean | null;
  /** Null for a catalog review, which has no photo. */
  photo: {
    url: string;
    preview_url: string | null;
    from: string | null;
    /** FS-807 — the upload's content hash; null for a photo from before hashes. */
    sha256?: string | null;
    /** FS-807 — the upload matched this photo already on the record. */
    duplicate_of?: string | null;
  } | null;
  /**
   * FS-806 — full view of a photo review only. `mode` is the server's
   * placement switch; `keep_required` says the photo cannot leave the record
   * it was dropped on (so "Also keep it" is checked and cannot be cleared).
   */
  placement?: {
    mode: boolean;
    source_kind: 'hvac' | 'ac';
    keep_required: PhotoKeepReason | null;
  } | null;
  /** FS-787 — only on catalog reviews. */
  kind?: PhotoReviewKind;
  catalog?: (CatalogReviewModel & {
    model_normalized: string | null;
    product_line: string | null;
    matched_via: 'model' | 'alias' | null;
  }) | null;
  /** FS-787 — how many earlier decisions a reopened catalog review carries. */
  history_count?: number;
  /** FS-787 — the Clip this catalog review can be applied from. */
  applies_from_serial?: string | null;
  /** FS-787 R4 — see PhotoReviewSummary. */
  binding?: CatalogReviewBinding | null;
  read: {
    component: string | null;
    basis: string | null;
    confidence: number | null;
    catalog_model_id: string | null;
    model_read: string | null;
    analyzed_at: string | null;
  } | null;
  hold: { reason: string } | null;
  stages: Record<string, unknown> | null;
  default_target_id: string | null;
  /** Absent from the status view. */
  targets?: PhotoReviewTarget[];
  applying: { apply_id: string; since: string | null; stuck: boolean } | null;
  last_apply: PhotoReviewLastApply | null;
  decision: PhotoReviewDecision | null;
  /**
   * The device summary refresh after an Apply. `status` is `pending` until it
   * has run (lists empty), then `done` or `failed`; null before it starts.
   */
  resync: { status?: 'pending' | 'done' | 'failed' | null; sent: string[]; failed: string[] } | null;
  /** `to` (FS-806) names the record a `photo_moved` review's photo went to. */
  superseded: { reason: string; to?: string | null } | null;
  /**
   * FS-876 — only on a held component-mismatch catalog review: what clearing
   * or moving its values would do, from the records as they are now. Null
   * once the review is closed; absent on every other review.
   */
  fix?: ReviewFix | null;
}

// ── FS-876: fixing a model on the wrong record ────────────────────────────
//
// A catalog review held because the catalog knows the record's model as
// another part of the system offers its fix beside Dismiss: clear the values
// from this record where the right one already has them, or move them there.
// Every rule is the server's; the dialog shows the rows, the ticks and the
// sentence the server worked out, and sends back fields and tokens only.

/** What one row of the fix does, as the server decided it. */
export type ReviewFixNote =
  | 'same_there'
  | 'empty_there'
  | 'differs_there'
  | 'no_such_field'
  | 'other_side'
  | 'capacity_source_person'
  | 'person_value';

export interface ReviewFixRow {
  field: string;
  /** The value on this (the wrong) record, and who put it there. */
  value: unknown;
  source: string | null;
  confirmed_by: string | null;
  /** The value on the record it belongs on; null when that has none. */
  right_value: unknown;
  right_source: string | null;
  effect: 'move' | 'clear' | 'locked';
  note: ReviewFixNote;
  state: 'selectable' | 'locked_required' | 'locked_person_value';
  default_checked: boolean;
  /**
   * The value was read from a label photo not known to be the one the model
   * came from, so it may be this record's own; the server leaves it unticked.
   */
  from_other_photo: boolean;
  /** Values that go with this one (a tonnage's capacity source). */
  companions: Array<{ field: string; value: unknown; effect: 'move' | 'clear' }>;
  /** Opaque: sent back so the server can tell the value moved. */
  current_token: string;
}

/**
 * FS-942 — a record's name the fix offers to change: the record the values
 * leave (named for the brand or model going), or the one a Move creates. The
 * server's suggestion; the dialog ticks it, never types it.
 */
export interface ReviewFixName {
  record: 'wrong' | 'right';
  kind: PhotoReviewTab;
  /** Its name now; for the record a Move creates, the name it gets unticked. */
  current: string;
  suggested: string;
  /**
   * `from_values`: the name is the brand or model leaving; `owner_saved`:
   * the same, but the owner's app saved it (unticked); `new_record`: the
   * record a Move creates.
   */
  note: 'from_values' | 'owner_saved' | 'new_record';
  default_checked: boolean;
  /**
   * The rows it is named for: it changes only with one of them ticked. Empty
   * for a created record named by type alone.
   */
  named_for: string[];
  /** Opaque: sent back so the server can tell the name changed. */
  current_token: string;
}

/** Why a held review offers no fix, only Dismiss. */
export type ReviewFixReason =
  | 'model_differs'
  | 'model_person_value'
  | 'model_gone'
  | 'record_removed'
  | 'no_component'
  | 'not_supported'
  | 'several_records'
  | 'no_record'
  | 'unavailable';

/** What stops the label photo going with the fix. */
export type ReviewFixPhotoBlock =
  | PhotoKeepReason
  | 'photo_removal_off'
  | 'photo_placement_off'
  | 'photo_already_removed'
  | 'also_on_other_record';

export interface ReviewFix {
  /** What the server's default ticks do; null when there is nothing to offer. */
  action: 'clear' | 'move' | null;
  reason: ReviewFixReason | null;
  /** The record the review is on, holding another component's values. */
  wrong: { kind: PhotoReviewTab | null; path: string | null };
  /** The record they belong on; `path` null when a Move creates it. */
  right: {
    kind: PhotoReviewTab | null;
    path: string | null;
    exists: boolean;
    model: string | null;
    model_person_decided: boolean;
  };
  rows: ReviewFixRow[];
  /**
   * FS-942 — the names the fix offers to change. Absent from a server before
   * names; read as none.
   */
  names?: ReviewFixName[];
  /**
   * FS-950 — whether the fix hands the catalog snapshot back, so the record
   * the model ends up on gets a catalog review. Absent from an older server.
   */
  catalog_review_after?: boolean;
  /**
   * The label photo the model was read from. It goes where the values go:
   * moved with a Move, removed ("wrong photo", with Undo) with a Clear.
   */
  photo: {
    url: string;
    default_checked: boolean;
    blocked: { remove: ReviewFixPhotoBlock | null; move: ReviewFixPhotoBlock | null };
  } | null;
  /** The system tonnage before and after, in the server's words. */
  consequence: {
    now: { tons: number; component: string | null; source: string | null } | null;
    /** With the tonnage row ticked (or with no tonnage row). */
    text: string;
    /** With the tonnage row unticked; null when there is none to untick. */
    text_without_tonnage: string | null;
  } | null;
}

/** What a fix sends: fields and tokens, the names ticked, the photo's choice, and the reason. */
export interface ReviewFixBody {
  edit_id: string;
  version: number;
  rows: Array<{ field: string; token: string }>;
  /** FS-942 — the ticked names, by record and token; no name text is sent. */
  names: Array<{ record: 'wrong' | 'right'; token: string }>;
  photo: { url: string; action: 'remove' | 'move' } | null;
  reason: string;
}

export interface ReviewFixResult {
  review_id: string;
  edit_id: string;
  status: PhotoReviewStatus;
  fix: 'cleared' | 'moved';
  wrong: { kind: 'hvac' | 'ac'; path: string };
  right: { kind: 'hvac' | 'ac'; path: string; created: boolean };
  moved: string[];
  cleared: string[];
  /** FS-942 — the names changed. Absent from a server before names. */
  renamed?: Array<{ record: 'wrong' | 'right'; path: string; from: string | null; to: string }>;
  /**
   * What happened to the photo, when it was ticked: moved with the values, or
   * removed through the photo removal (`removal`, with its Undo), or left
   * where it was because the removal was refused (`error`).
   */
  photo: {
    url: string;
    action: 'moved' | 'removed' | 'not_removed' | 'remove';
    removal?: PhotoRemovalResult | null;
    error?: { code: string | null; message: string } | null;
  } | null;
  reviews_superseded: string[];
  /**
   * FS-950 — the catalog job the fix wrote, and the record whose catalog
   * review it raises. Null when none was written; absent from an older server.
   */
  catalog_proposal?: { id: string; model_id?: number; record: { kind: 'hvac' | 'ac'; path: string } } | null;
  sync?: { enqueued: boolean } | null;
  audit_mirrored?: boolean | null;
}

/** FS-876 — a dismissed held review the record list offers to review again. */
export interface DismissedReviewSummary extends PhotoReviewSummary {
  dismissed_at: string | null;
}

/**
 * What Apply sends. Ids and tokens only — there is deliberately no field here
 * that could carry a value or a stamp, and a test pins that.
 */
export interface PhotoReviewApplyBody {
  apply_id: string;
  version: number;
  target_id: string;
  selected: Array<{ candidate_id: string; current_token: string }>;
  reason: string;
  /**
   * FS-806 — leave the photo on the record it was dropped on as well. A
   * choice about the photo, not a value: sent only when the dialog shows the
   * "Also keep it" box, and omitted otherwise (the server reads absent as
   * false, and ignores it for a catalog review or a same-record Apply).
   */
  keep_on_source?: boolean;
}

export interface PhotoReviewApplyResult {
  review_id: string;
  apply_id: string;
  status: PhotoReviewStatus;
}

export interface PhotoReviewDeclineBody {
  edit_id: string;
  version: number;
  reason?: string;
}

export interface PhotoReviewDeclineResult {
  review_id: string;
  status: PhotoReviewStatus;
  open: boolean;
}

/**
 * Whether the device summary refresh after an Apply has finished, and how.
 * No resync object yet, or `status: "pending"`, is not finished. `done` and
 * `failed` are. A resync object whose `status` is null is a legacy review
 * that recorded no status (contract addendum 2): finished, and failed only
 * when its list of failures says so.
 */
export function resyncOutcome(resync: PhotoReview['resync']): 'pending' | 'ok' | 'failed' {
  if (!resync) return 'pending';
  if (resync.status === 'pending') return 'pending';
  if (resync.status === 'failed' || resync.failed.length > 0) return 'failed';
  return 'ok';
}

const reviewUrl = (serial: string, reviewId: string) =>
  `${equipmentUrl(serial)}/photo-reviews/${encodeURIComponent(reviewId)}`;

/**
 * One review. `statusOnly` asks for the top level without the targets — one
 * document read on the server, which is what every poll uses; the full view
 * reads each target as it is now and is fetched when the dialog opens.
 */
export async function getPhotoReview(
  serial: string,
  reviewId: string,
  opts: { statusOnly?: boolean } = {},
): Promise<PhotoReview> {
  const res = await axiosInstance.get<PhotoReview>(
    reviewUrl(serial, reviewId),
    opts.statusOnly ? { params: { view: 'status' } } : undefined,
  );
  return res.data;
}

export async function applyPhotoReview(
  serial: string,
  reviewId: string,
  body: PhotoReviewApplyBody,
): Promise<PhotoReviewApplyResult> {
  const res = await axiosInstance.post<PhotoReviewApplyResult>(
    `${reviewUrl(serial, reviewId)}/apply`,
    body,
  );
  return res.data;
}

/**
 * FS-787 — a short-lived link to our stored copy of a manufacturer document,
 * the same call the Equipment Catalog page makes.
 */
export async function getEquipmentDocumentFileUrl(documentId: number): Promise<string> {
  const res = await axiosInstance.get<{ url?: string }>(
    `${config.devicesApiUrl}/equipment/documents/${documentId}/file`,
  );
  const url = res.data?.url;
  if (!url) throw new Error('the catalog returned no link for this document');
  return url;
}

/**
 * FS-787 R4 — move a catalog review whose Clip left the system to the Clip
 * whose drawer is open (`serial`). The server checks both registrations.
 */
export async function rebindCatalogReview(
  serial: string,
  reviewId: string,
): Promise<{ review_id: string; changed: boolean; version: number | null; binding: CatalogReviewBinding }> {
  const res = await axiosInstance.post(`${reviewUrl(serial, reviewId)}/rebind`, { deviceSerial: serial });
  return res.data;
}

export async function declinePhotoReview(
  serial: string,
  reviewId: string,
  body: PhotoReviewDeclineBody,
): Promise<PhotoReviewDeclineResult> {
  const res = await axiosInstance.post<PhotoReviewDeclineResult>(
    `${reviewUrl(serial, reviewId)}/decline`,
    body,
  );
  return res.data;
}

export async function fixCatalogReview(
  serial: string,
  reviewId: string,
  body: ReviewFixBody,
): Promise<ReviewFixResult> {
  const res = await axiosInstance.post<ReviewFixResult>(`${reviewUrl(serial, reviewId)}/fix`, body);
  return res.data;
}

/** FS-876 — open a dismissed held review again ("Review again"). */
export async function reopenCatalogReview(
  serial: string,
  reviewId: string,
  version: number,
): Promise<{ review_id: string; changed: boolean; status: PhotoReviewStatus; open: boolean; version: number | null }> {
  const res = await axiosInstance.post(`${reviewUrl(serial, reviewId)}/reopen`, { version });
  return res.data;
}

const removalUrl = (serial: string, removalId: string) =>
  `${equipmentUrl(serial)}/photos/remove/${encodeURIComponent(removalId)}`;

/**
 * FS-783 — take one photo off every record on the system that carries it.
 * The server works out which records those are inside its own transaction;
 * `target` only says which record the operator pressed Remove on.
 */
export async function removePhoto(serial: string, body: PhotoRemovalBody): Promise<PhotoRemovalResult> {
  const res = await axiosInstance.post<PhotoRemovalResult>(`${equipmentUrl(serial)}/photos/remove`, body);
  return res.data;
}

// ── FS-1079: which way up a label photo is drawn (Rotate) ────────────────

export interface PhotoRotateBody {
  /** A fresh id per press; a network retry of the same press reuses it. */
  edit_id: string;
  url: string;
  /** The new clockwise turn: 0, 90, 180 or 270. */
  degrees: number;
  /** The record the operator pressed Rotate on. */
  target: { kind: EquipmentTargetKind; path: string };
}

export interface PhotoRotateResult {
  edit_id: string;
  url: string;
  degrees: number;
  source: 'operator';
  /** Every record the turn was stored on: each one that carries the photo. */
  records: string[];
}

/**
 * FS-1079 — store which way up a label photo is drawn, on every record of
 * the system that carries it. The photo file is never changed.
 */
export async function rotatePhoto(serial: string, body: PhotoRotateBody): Promise<PhotoRotateResult> {
  const res = await axiosInstance.post<PhotoRotateResult>(`${equipmentUrl(serial)}/photos/rotate`, body);
  return res.data;
}

// ── FS-806: taking a photo off one record alone (Detach) ─────────────────

export interface PhotoDetachBody {
  /** A fresh id per attempt; a network retry of the same attempt reuses it. */
  edit_id: string;
  url: string;
  /** The record the photo comes off. */
  from_target: { kind: EquipmentTargetKind; path: string };
  reason: string;
}

/**
 * What a Detach did. `to` is the record the server chose to carry the
 * photo's read with it: another record that already carries the photo (the
 * first of the GET's `also_on`). A refusal by the keep rule is a 409 whose
 * code is the reason (`source_values_cite_photo` | `photo_busy`) and whose
 * body names the record the photo stayed on (`stayed_on`).
 */
export interface PhotoDetachResult {
  detach_id?: string;
  url: string;
  from: string;
  to: string;
  to_kind?: EquipmentTargetKind;
  /** The URL the photo stays as on `to`: `url`, or another upload of the same bytes. */
  stays_as?: string;
  /** The pieces taken off `from`, by field name. */
  moved?: string[];
  envelopes_moved?: number;
  evidence_rekeyed?: Array<{ path: string; field: string }>;
  /** Other open reviews of the photo on `from` that the move closed. */
  reviews_superseded?: string[];
  resync?: { sent: boolean; failed: string | null } | null;
}

/**
 * FS-806 — take one photo off one record while another record on the system
 * still carries it. No tombstone and no Undo: it is a move, and the photo is
 * still on the other record.
 */
export async function detachPhoto(serial: string, body: PhotoDetachBody): Promise<PhotoDetachResult> {
  const res = await axiosInstance.post<PhotoDetachResult>(`${equipmentUrl(serial)}/photos/detach`, body);
  return res.data;
}

/** FS-783 — put a removed photo back, piece by piece, while its Undo window is open. */
export async function undoPhotoRemoval(
  serial: string,
  removalId: string,
  body: PhotoUndoBody,
): Promise<PhotoUndoResult> {
  const res = await axiosInstance.post<PhotoUndoResult>(`${removalUrl(serial, removalId)}/undo`, body);
  return res.data;
}

/** Addendum 2 F9 — hide a removal notice for good; nothing on the record changes. */
export async function dismissPhotoRemovalNotice(
  serial: string,
  removalId: string,
  noticeId: string,
): Promise<void> {
  await axiosInstance.post(`${removalUrl(serial, removalId)}/notices/${encodeURIComponent(noticeId)}/dismiss`, {});
}

/**
 * FS-783 — try the file's protection again: set it on a removed photo whose
 * hold failed, or release it on a restored one. Changes no record, so it
 * writes no audit and needs no reason.
 */
export async function retryPhotoProtection(
  serial: string,
  removalId: string,
  body: { url: string },
): Promise<PhotoStorageState> {
  const res = await axiosInstance.post<PhotoStorageState>(`${removalUrl(serial, removalId)}/hold`, body);
  return res.data;
}

/**
 * Brands already in the filter catalog, offered as suggestions over a free-text
 * box — a filter nobody has catalogued yet still has to be recordable. Same
 * route the Filter Catalog page reads.
 */
export async function getFilterBrands(): Promise<string[]> {
  const res = await axiosInstance.get<string[]>(`${config.filterCatalogApiUrl ?? ''}/brands`);
  return Array.isArray(res.data) ? res.data : [];
}

interface ServerRefusal {
  response?: {
    status?: number;
    data?: { code?: string; message?: string; claims?: EquipmentClaim[]; [key: string]: unknown };
  };
}

const refusal = (err: unknown): ServerRefusal['response'] =>
  (err as ServerRefusal | null)?.response;

/**
 * True when the server answered (a refusal), false when nothing came back.
 * A request that got no answer may still have been applied, so its retry
 * reuses the same edit id; one the server refused is over.
 */
export function serverAnswered(err: unknown): boolean {
  return refusal(err) !== undefined;
}

/** The server's machine-readable refusal code, when it sent one. */
export function equipmentErrorCode(err: unknown): string | null {
  return refusal(err)?.data?.code ?? null;
}

/**
 * The extra keys a refusal carries. firebase-api sends its error bodies flat
 * — `{code, message, ...details}` — so `stale_fields` arrives as
 * `{code, message, fields, review}` and these are read off the body itself.
 */
export function equipmentErrorDetails(err: unknown): Record<string, unknown> | null {
  const data = refusal(err)?.data;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

/** The claim list a 409 `ambiguous_claims` carries, so the drawer can list them. */
export function equipmentErrorClaims(err: unknown): EquipmentClaim[] {
  const claims = refusal(err)?.data?.claims;
  return Array.isArray(claims) ? claims : [];
}

/**
 * A refusal in words. An unrecognized code is quoted rather than swallowed —
 * a code nobody has copy for is still worth reading — and no branch ever
 * reduces to "it failed": each one says that nothing was saved.
 */
export function equipmentErrorMessage(err: unknown): string {
  const response = refusal(err);
  if (!response) {
    return 'The server could not be reached, so nothing was saved. Check the connection and try again.';
  }
  const code = response.data?.code;
  const message = response.data?.message;
  const held = capacityHeldMessage(code, (response.data as { fields?: string[] } | undefined)?.fields);
  if (held) return held;
  if (code && SERVER_WORDED_CODES.has(code) && typeof message === 'string' && message.trim()) {
    const said = message.trim();
    return `${/[.!?]$/.test(said) ? said : `${said}.`} Nothing was saved.`;
  }
  const worded = own(EQUIPMENT_EDIT_ERRORS, code);
  if (worded) return worded;
  if (response.status === 403) {
    return 'Your account does not have permission to edit equipment records.';
  }
  if (response.status === 404) {
    return 'This device has no app records to edit, or it is not in your scope.';
  }
  if (code) return `The server rejected the change (${code}). Nothing was saved.`;
  return `The server rejected the change (HTTP ${response.status ?? 'no status'}). Nothing was saved.`;
}

/**
 * A failed photo upload in words.
 *
 * The generic "could not be reached" is wrong for this route often enough to
 * mislead: a photo that never gets a response has usually been stopped at the
 * edge for its size, not by a connection that is down — the rest of the drawer
 * is plainly still talking to the server. So the wording names the file and
 * its size and points at the size, and only a refusal the server actually sent
 * gets the server's own sentence.
 */
export function photoUploadErrorMessage(
  err: unknown,
  file: { name: string; size: number },
): string {
  if (!refusal(err)) {
    return `The upload of ${file.name} (${photoSizeLabel(file.size)}) was blocked before it reached the server. Try a smaller image or contact support.`;
  }
  // FS-1078 — one read of a photo at a time: the file is a photo this record
  // already has, and a read or Apply of it is still running. Said without
  // claiming nothing was stored: when another upload of the same bytes won
  // the race, this one's file was saved and is kept, attached to nothing.
  if (equipmentErrorCode(err) === 'photo_busy') {
    return `${file.name} is a photo this record already has, and a read or Apply of it is still running. No photo was added to this record and no read was queued; try again once that has finished.`;
  }
  return equipmentErrorMessage(err);
}

/** A refusal from a photo removal, Undo, protection retry or notice dismissal, in words. */
export function photoRemovalErrorMessage(err: unknown): string {
  const code = equipmentErrorCode(err);
  const removal = own(PHOTO_REMOVAL_ERRORS, code);
  if (removal) return removal;
  return equipmentErrorMessage(err);
}

/** FS-1078 — the Read again refusals, in the operator's words. */
const PHOTO_REREAD_ERRORS: Record<string, string> = {
  photo_busy: 'This photo is still being read or applied. Try Read again once that has finished.',
  photo_changed: "The record's photos changed while the read was being asked for. Nothing was read; try again.",
  photo_not_found: 'This record no longer has that photo. Nothing was read.',
  photo_on_another_record: 'That photo is now on another record. Read it again from there.',
  photo_review_off: 'Read again needs photo reviews, which are switched off on the server. Nothing was read.',
  too_many_reads: 'This photo has been read too many times on this record to read it again here.',
};

/** A refusal from Read again, in words. */
export function photoRereadErrorMessage(err: unknown): string {
  const worded = own(PHOTO_REREAD_ERRORS, equipmentErrorCode(err));
  if (worded) return worded;
  return equipmentErrorMessage(err);
}

/** True only for a real calendar day written exactly as YYYY-MM-DD. */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map((part) => Number(part));
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}
